import React, {
  ReactNode,
  InputHTMLAttributes,
  ChangeEvent,
  useRef,
  MouseEvent,
} from 'react';
import cn from 'classnames';
import { UploadFileConfig, uploadFileToQiniu } from '../../utils/fileHelper';

enum StatusStr {
  DONE = 'done',
  UPLOADING = 'uploading',
  ERROE = 'error',
}
interface UploadedFile {
  status: StatusStr.DONE;
  url: string;
  file?: File;
  files?: FileList;
}

export type UploadFile =
  | UploadedFile
  | {
      status: StatusStr.UPLOADING | StatusStr.ERROE;
      file?: File;
      files?: FileList;
    };

interface Props
  extends Pick<
    InputHTMLAttributes<HTMLInputElement>,
    'accept' | 'multiple' | 'id' | 'multiple'
  > {
  className?: string;
  children: ReactNode;
  options?: Partial<UploadFileConfig>;
  onChange: (file: UploadFile | UploadFile[]) => void;
  disable?: boolean;
}

function Upload(props: Props) {
  const { children, className, onChange, options, disable, ...rest } = props;
  const containerCls = cn(className);
  const fileInput = useRef<HTMLInputElement>(null);

  const resetValue = () => {
    if (fileInput.current) {
      fileInput.current.value = '';
    }
  };

  const uploadFiles = async (files: FileList) => {
    const defaultOptions: UploadFileConfig = {
      scope: 'pub-med-casem',
    };

    try {
      onChange({ files, status: StatusStr.UPLOADING });
      const uploadAsyncMaps = Array.from(files).map((file: File) => {
        return uploadFileToQiniu(file, { ...defaultOptions, ...options });
      });
      const uploadResult = await Promise.all(uploadAsyncMaps);
      const uploadSuccessData = uploadResult.map(res => {
        return { file: res.file, status: StatusStr.DONE, url: res.fullUrl };
      });

      onChange(props.multiple ? uploadSuccessData : uploadSuccessData[0]);
    } catch (err) {
      onChange({ files, status: StatusStr.ERROE });
    } finally {
      resetValue();
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;

    if (files && files.length > 0) {
      uploadFiles(files);
    }
  };

  const handleClick = (e: MouseEvent<HTMLSpanElement>) => {
    if (disable) {
      return;
    }
    if (fileInput.current && e.target !== fileInput.current) {
      fileInput.current.click();
    }
  };

  return (
    <span className={containerCls} onClick={handleClick}>
      <input
        {...rest}
        ref={fileInput}
        type="file"
        style={{ display: 'none' }}
        onChange={handleChange}
      />
      {children}
    </span>
  );
}

export default Upload;
